import type { ColumnDef, FilterFn } from '@tanstack/react-table';
import { useState, useMemo, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '@/feature/auth/hooks/useAuth';
import { organizationService } from '@/feature/organization/services/organizationService';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import ConfirmModal from '@/shared/components/ui/Modals/ConfirmModal';
import { formatDate } from '@/shared/utils/dateUtils';
import { tokenStorage } from '@/shared/utils/tokenStorage';

import { ROUTES } from '../../routes/routeConstants';
import StatusBadge from '../common/StatusBadge';

import AddOrganizationModal from './AddOrganizationModal';
import EditOrganizationModal from './EditOrganizationModal';
import ResendActivationModal from './ResendActivationModal';

interface IOrganization {
  id: number;
  apiId?: string; // Store original API ID for navigation (optional for backward compatibility)
  organizationName: string;
  companyName: string;
  industry: string;
  email: string;
  adminName?: string;
  users: number;
  projects_count: number;
  test_cases_count: number;
  status: 'Active' | 'Inactive';
  created: string;
  description?: string;
  subscription_plan?: string; // Store subscription plan from API
  country?: string;
}

// Helper function to extract field value from API response with multiple name variations
const extractField = (obj: Record<string, unknown>, fieldName: string): string => {
  // Try exact match first
  if (obj[fieldName] && typeof obj[fieldName] === 'string') {
    return obj[fieldName] as string;
  }

  // Try camelCase
  const camelCase = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
  if (obj[camelCase] && typeof obj[camelCase] === 'string') {
    return obj[camelCase] as string;
  }

  // Try PascalCase
  const pascalCase = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  if (obj[pascalCase] && typeof obj[pascalCase] === 'string') {
    return obj[pascalCase] as string;
  }

  // Try UPPERCASE
  const upperCase = fieldName.toUpperCase();
  if (obj[upperCase] && typeof obj[upperCase] === 'string') {
    return obj[upperCase] as string;
  }

  // Try snake_case
  const snakeCase = fieldName.replace(/([A-Z])/g, '_$1').toLowerCase();
  if (obj[snakeCase] && typeof obj[snakeCase] === 'string') {
    return obj[snakeCase] as string;
  }

  // Try case-insensitive search
  const lowerFieldName = fieldName.toLowerCase();
  for (const key in obj) {
    if (key.toLowerCase() === lowerFieldName && typeof obj[key] === 'string') {
      return obj[key] as string;
    }
  }

  return '';
};

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<{
    org: IOrganization;
    action: 'deactivate';
  } | null>(null);
  const [pendingActivation, setPendingActivation] = useState<IOrganization | null>(null);
  const [isSendingActivation, setIsSendingActivation] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<IOrganization | null>(null);
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Redirect to login if not authenticated (after auth check completes)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const token = tokenStorage.getAccessToken();
      if (!token) {
        navigate(ROUTES.LOGIN, { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch organizations from API
  const fetchOrganizations = useCallback(async () => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (hasFetched) {
      return;
    }

    setIsLoading(true);
    setHasFetched(true);
    try {
      const apiOrganizations = await organizationService.getOrganizations();

      // Map API response to the format expected by the UI
      const mappedOrganizations: IOrganization[] = apiOrganizations.map((org, index) => {
        // Store original API ID (string) for navigation
        const apiId = typeof org.id === 'string' ? org.id : String(org.id);

        // Convert id to number for display/table purposes
        let orgId: number;
        if (typeof org.id === 'string') {
          const parsed = parseInt(org.id, 10);
          orgId = isNaN(parsed) ? index + 1 : parsed;
        } else {
          orgId = typeof org.id === 'number' ? org.id : index + 1;
        }

        // Get org_name from API response (can be org_name, name, or organizationName)
        const orgName =
          (org as { org_name?: string }).org_name || org.organizationName || org.name || '';

        // Get company name (check for both camelCase and snake_case)
        const companyName =
          (org as { companyName?: string; company_name?: string }).companyName ||
          (org as { companyName?: string; company_name?: string }).company_name ||
          '--';

        // Get industry and email using helper function to check all possible field name variations
        const orgAny = org as Record<string, unknown>;
        const industry = extractField(orgAny, 'industry');
        const email = extractField(orgAny, 'email');

        let userCount = 0;
        if (Array.isArray(org.users)) {
          userCount = org.users.length;
        } else if (typeof org.users === 'number') {
          userCount = org.users;
        }

        const projectsCount = typeof orgAny.projects_count === 'number' ? orgAny.projects_count : 0;
        const testCasesCount =
          typeof orgAny.test_cases_count === 'number' ? orgAny.test_cases_count : 0;

        return {
          id: orgId,
          apiId: apiId, // Store original API ID
          organizationName: orgName,
          companyName: companyName,
          industry: industry || 'N/A',
          email: email || 'N/A',
          adminName: String(
            (org as Record<string, unknown>)['admin_name'] ||
              (org as Record<string, unknown>)['adminName'] ||
              ''
          ),
          users: userCount,
          projects_count: projectsCount,
          test_cases_count: testCasesCount,
          status: (org.status === 'Active' || org.is_active === true ? 'Active' : 'Inactive') as
            | 'Active'
            | 'Inactive',
          created:
            org.created || formatDate(org.created_at ? new Date(org.created_at) : new Date()),
          description: org.description || '',
          subscription_plan:
            (org as { subscription_plan?: string }).subscription_plan || org.subscription_plan,
          country: (org as { country?: string }).country || '',
        };
      });

      setOrganizations(mappedOrganizations);
    } catch {
      // Error is already handled by organizationService
      setHasFetched(false); // Allow retry on error
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading, hasFetched]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Reset fetch flag when authentication changes
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      setHasFetched(false);
    }
  }, [isAuthenticated, authLoading]);

  const handleAddOrganization = async (newOrg: {
    organizationName: string;
    companyName: string;
    industry: string;
    email: string;
    adminName: string;
    country: string;
    status: 'Active' | 'Inactive';
    description: string;
  }) => {
    try {
      // Generate org_id from organization name or use company name
      const orgId = newOrg.companyName
        ? `ORG${newOrg.companyName.toUpperCase().replace(/\s+/g, '').substring(0, 8)}${Date.now().toString().slice(-4)}`
        : `ORG${newOrg.organizationName.toUpperCase().replace(/\s+/g, '').substring(0, 8)}${Date.now().toString().slice(-4)}`;

      // Map UI form data to API request format
      const createRequest = {
        org_id: orgId,
        org_name: newOrg.organizationName,
        admin_name: newOrg.adminName,
        company_name: newOrg.companyName || '',
        industry: newOrg.industry || '',
        email: newOrg.email || '',
        description: newOrg.description || '',
        country: newOrg.country,
        org_logo: '', // Optional, can be added later
        timezone: 'America/New_York', // Default value, can be made configurable
        subscription_plan: 'basic', // Default value, can be made configurable
        subscription_expires_at: new Date(
          new Date().setFullYear(new Date().getFullYear() + 1)
        ).toISOString(), // Default to 1 year from now
        is_active: false,
        show_selenium_script: false, // Default value
        ai_provider: 'openai', // Default value, can be made configurable
        ai_model: 'gpt-4', // Default value, can be made configurable
        ai_api_key: '', // Optional, can be added later
      };

      const createdOrg = await organizationService.createOrganization(createRequest);

      if (createdOrg) {
        // Automatically send activation email if organization has a valid email

        // Optimistically add the new org to the list so it appears immediately
        const newOrgEntry: IOrganization = {
          id:
            typeof createdOrg.id === 'number'
              ? createdOrg.id
              : parseInt(String(createdOrg.id), 10) || Date.now(),
          apiId: String(createdOrg.id),
          organizationName: newOrg.organizationName,
          companyName: newOrg.companyName || '--',
          industry: newOrg.industry || 'N/A',
          email: newOrg.email || 'N/A',
          adminName: newOrg.adminName || '',
          users: 0,
          projects_count: 0,
          test_cases_count: 0,
          status: 'Inactive',
          created: formatDate(new Date()),
          description: newOrg.description || '',
          country: newOrg.country || '',
        };
        setOrganizations((prev) => [newOrgEntry, ...prev]);
        setIsModalOpen(false);
        toast.success('Organization created successfully');
      } else {
        // createdOrg is null — the service caught an error (and already showed a toast),
        // but the backend may have persisted the org anyway (e.g. 5xx after write).
        // Close the modal and refetch so the list reflects the real server state.
        setIsModalOpen(false);
        await refetchOrganizations();
      }
    } catch {
      // Error is already handled by organizationService
      // Don't close modal on error so user can retry
    }
  };

  const handleUpdateOrganization = async (updatedOrg: IOrganization) => {
    try {
      const orgId = updatedOrg.apiId || String(updatedOrg.id);

      // Fetch current organization data to get all fields (PUT may require full resource)
      const currentOrg = await organizationService.getOrganizationById(orgId);

      // Helper function to normalize values - convert "N/A" to empty string
      const normalizeValue = (value: string | undefined): string => {
        if (!value || value === 'N/A' || value.trim() === 'N/A') {
          return '';
        }
        return value.trim();
      };

      // Map UI form data to API request format
      // Ensure industry and email are explicitly set (even if empty) so API processes them
      const normalizedIndustry = normalizeValue(updatedOrg.industry);
      const normalizedEmail = normalizeValue(updatedOrg.email);

      // Build update request - merge current org data with updates
      // This ensures we send all required fields for PUT request
      const updateRequest: Partial<{
        org_id?: string;
        org_name: string;
        admin_name: string;
        company_name: string;
        industry: string;
        email: string;
        description: string;
        is_active: boolean;
        subscription_plan?: string;
        country?: string;
        timezone?: string;
      }> = {
        org_name: updatedOrg.organizationName,
        admin_name: updatedOrg.adminName || '',
        company_name: updatedOrg.companyName || updatedOrg.organizationName,
        industry: normalizedIndustry,
        email: normalizedEmail,
        description: updatedOrg.description || '',
        is_active: updatedOrg.status === 'Active',
      };

      // Merge in existing fields from current organization if available
      if (currentOrg) {
        // Preserve fields that aren't being updated
        if (currentOrg.org_id && typeof currentOrg.org_id === 'string') {
          updateRequest.org_id = currentOrg.org_id;
        }
        if (currentOrg.subscription_plan) {
          updateRequest.subscription_plan = currentOrg.subscription_plan;
        }
        if (updatedOrg.country) {
          updateRequest.country = updatedOrg.country;
        } else if (currentOrg.country) {
          updateRequest.country = currentOrg.country as string;
        }
        if (currentOrg.timezone) {
          updateRequest.timezone = currentOrg.timezone as string;
        }
      } else if (updatedOrg.subscription_plan) {
        // Fallback to stored value if we couldn't fetch
        updateRequest.subscription_plan = updatedOrg.subscription_plan;
      }

      const updated = await organizationService.updateOrganization(orgId, updateRequest);

      if (updated) {
        // Close modal on success
        setIsEditModalOpen(false);
        setSelectedOrganization(null);
        // Reset fetch flag to allow refetch
        setHasFetched(false);
        // Refresh the list to show the updated organization
        await fetchOrganizations();
      }
    } catch {
      // Error is already handled by organizationService
      // Don't close modal on error so user can retry
    }
  };

  const handleEditClickCallback = useCallback(async (org: IOrganization) => {
    const orgId = String(org.apiId ?? org.id);
    try {
      const detail = await organizationService.getOrganizationById(orgId);
      if (detail) {
        const adminName = String(
          (detail as Record<string, unknown>)['admin_name'] ||
            (detail as Record<string, unknown>)['adminName'] ||
            org.adminName ||
            ''
        );
        setSelectedOrganization({ ...org, adminName });
      } else {
        setSelectedOrganization(org);
      }
    } catch {
      setSelectedOrganization(org);
    }
    setIsEditModalOpen(true);
  }, []);

  const refetchOrganizations = useCallback(async () => {
    if (authLoading || !isAuthenticated) {
      return;
    }

    setIsLoading(true);
    try {
      const apiOrganizations = await organizationService.getOrganizations();

      // Map API response to the format expected by the UI
      const mappedOrganizations: IOrganization[] = apiOrganizations.map((org, index) => {
        // Store original API ID (string) for navigation
        const apiId = typeof org.id === 'string' ? org.id : String(org.id);

        // Convert id to number for display/table purposes
        let orgId: number;
        if (typeof org.id === 'string') {
          const parsed = parseInt(org.id, 10);
          orgId = isNaN(parsed) ? index + 1 : parsed;
        } else {
          orgId = typeof org.id === 'number' ? org.id : index + 1;
        }

        // Get org_name from API response (can be org_name, name, or organizationName)
        const orgName =
          (org as { org_name?: string }).org_name || org.organizationName || org.name || '';

        // Get company name (check for both camelCase and snake_case)
        const companyName =
          (org as { companyName?: string; company_name?: string }).companyName ||
          (org as { companyName?: string; company_name?: string }).company_name ||
          '--';

        // Get industry and email using helper function to check all possible field name variations
        const orgAny = org as Record<string, unknown>;
        const industry = extractField(orgAny, 'industry');
        const email = extractField(orgAny, 'email');

        let userCount = 0;
        if (Array.isArray(org.users)) {
          userCount = org.users.length;
        } else if (typeof org.users === 'number') {
          userCount = org.users;
        }

        const projectsCount = typeof orgAny.projects_count === 'number' ? orgAny.projects_count : 0;
        const testCasesCount =
          typeof orgAny.test_cases_count === 'number' ? orgAny.test_cases_count : 0;

        return {
          id: orgId,
          apiId: apiId, // Store original API ID
          organizationName: orgName,
          companyName: companyName,
          industry: industry || 'N/A',
          email: email || 'N/A',
          adminName: String(
            (org as Record<string, unknown>)['admin_name'] ||
              (org as Record<string, unknown>)['adminName'] ||
              ''
          ),
          users: userCount,
          projects_count: projectsCount,
          test_cases_count: testCasesCount,
          status: (org.status === 'Active' || org.is_active === true ? 'Active' : 'Inactive') as
            | 'Active'
            | 'Inactive',
          created:
            org.created || formatDate(org.created_at ? new Date(org.created_at) : new Date()),
          description: org.description || '',
          subscription_plan:
            (org as { subscription_plan?: string }).subscription_plan || org.subscription_plan,
          country: (org as { country?: string }).country || '',
        };
      });

      setOrganizations(mappedOrganizations);
    } catch {
      // Error is already handled by organizationService
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const getAdminNameFromStorage = useCallback((): string => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) {
        return '';
      }

      const user = JSON.parse(raw) as Record<string, unknown>;

      if (typeof user.admin_name === 'string' && user.admin_name.trim()) {
        return user.admin_name.trim();
      }

      const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
      if (fullName) {
        return fullName;
      }

      if (typeof user.name === 'string' && user.name.trim()) {
        return user.name.trim();
      }

      if (typeof user.username === 'string' && user.username.trim()) {
        return user.username.trim();
      }

      return '';
    } catch {
      return '';
    }
  }, []);

  const handleDeactivateOrganization = useCallback((org: IOrganization) => {
    setPendingConfirm({ org, action: 'deactivate' });
  }, []);

  const handleActivateOrganization = useCallback(async (org: IOrganization) => {
    const orgId = String(org.apiId ?? org.id);
    try {
      const detail = await organizationService.getOrganizationById(orgId);
      if (detail) {
        const adminName = String(
          (detail as Record<string, unknown>)['admin_name'] ||
            (detail as Record<string, unknown>)['adminName'] ||
            org.adminName ||
            ''
        );
        setPendingActivation({ ...org, adminName });
      } else {
        setPendingActivation(org);
      }
    } catch {
      setPendingActivation(org);
    }
  }, []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!pendingConfirm) {
      return;
    }
    const { org } = pendingConfirm;
    const orgId = org.apiId || String(org.id);

    const normalizeValue = (value: string | undefined): string => {
      if (!value || value === 'N/A' || value.trim() === 'N/A') {
        return '';
      }
      return value.trim();
    };

    setIsLoading(true);
    try {
      const currentOrg = await organizationService.getOrganizationById(orgId);
      const updateRequest: Partial<{
        org_id?: string;
        org_name: string;
        company_name: string;
        industry: string;
        email: string;
        description: string;
        is_active: boolean;
        subscription_plan?: string;
        country?: string;
        timezone?: string;
      }> = { is_active: false };

      if (currentOrg) {
        updateRequest.org_name = currentOrg.org_name || org.organizationName;
        updateRequest.company_name =
          (currentOrg as { company_name?: string }).company_name || org.companyName;
        updateRequest.industry = normalizeValue(currentOrg.industry || org.industry);
        updateRequest.email = normalizeValue(currentOrg.email || org.email);
        updateRequest.description = currentOrg.description || org.description || '';
        if (currentOrg.org_id && typeof currentOrg.org_id === 'string') {
          updateRequest.org_id = currentOrg.org_id;
        }
        if (currentOrg.subscription_plan) {
          updateRequest.subscription_plan = currentOrg.subscription_plan;
        }
        if (currentOrg.country) {
          updateRequest.country = currentOrg.country as string;
        }
        if (currentOrg.timezone) {
          updateRequest.timezone = currentOrg.timezone as string;
        }
      } else {
        updateRequest.org_name = org.organizationName;
        updateRequest.company_name = org.companyName;
        updateRequest.industry = normalizeValue(org.industry);
        updateRequest.email = normalizeValue(org.email);
        updateRequest.description = org.description || '';
        if (org.subscription_plan) {
          updateRequest.subscription_plan = org.subscription_plan;
        }
      }

      const updated = await organizationService.updateOrganization(orgId, updateRequest);
      if (updated) {
        setHasFetched(false);
        await refetchOrganizations();
      }
    } finally {
      setIsLoading(false);
    }
  }, [pendingConfirm, refetchOrganizations]);

  const handleActivationSubmit = useCallback(
    async (formData: { email: string; admin_name: string }) => {
      if (!pendingActivation) {
        return;
      }
      const orgId = pendingActivation.apiId || String(pendingActivation.id);

      setIsSendingActivation(true);
      try {
        const ok = await organizationService.resendActivationMail(orgId, formData);
        if (ok) {
          toast.success(
            'Activation email sent. Organization will activate once the email is accepted.'
          );
          setPendingActivation(null);
        }
      } finally {
        setIsSendingActivation(false);
      }
    },
    [pendingActivation]
  );

  const columns = useMemo<ColumnDef<IOrganization>[]>(
    () => [
      {
        accessorKey: 'organizationName',
        header: 'Org Name',
        cell: (info) => (
          <span className='font-normal text-gray-900'>{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'companyName',
        header: 'Company Name',
      },
      {
        accessorKey: 'industry',
        header: 'Industry',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'users',
        header: 'Users',
        cell: (info) => (
          <div className='text-left w-full'>
            <button
              type='button'
              className='text-theme-500 underline font-normal hover:opacity-80 transition-opacity inline-block cursor-pointer bg-transparent border-none p-0'
              onClick={() =>
                navigate(`/organizations/${info.row.original.apiId || info.row.original.id}`)
              }
            >
              {info.getValue() as number}
            </button>
          </div>
        ),
      },
      {
        accessorKey: 'projects_count',
        header: '#Projects',
        cell: (info) => <span className='text-gray-900'>{info.getValue() as number}</span>,
      },
      {
        accessorKey: 'test_cases_count',
        header: '#Test Cases',
        cell: (info) => <span className='text-gray-900'>{info.getValue() as number}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue() as 'Active' | 'Inactive'} />,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: (info) => {
          const org = info.row.original;
          const viewBtn =
            'inline-flex items-center justify-center p-1.5 rounded-md text-theme-600 hover:text-theme-700 hover:bg-theme-50/90 bg-transparent border-0 cursor-pointer transition-colors';
          const editBtn =
            'inline-flex items-center justify-center p-1.5 rounded-md text-sky-600 hover:text-sky-700 hover:bg-sky-50/80 bg-transparent border-0 cursor-pointer transition-colors';
          const deactivateBtn =
            'inline-flex items-center justify-center p-1.5 rounded-md text-amber-600 hover:text-amber-700 hover:bg-amber-50/80 bg-transparent border-0 cursor-pointer transition-colors';
          const activateBtn =
            'inline-flex items-center justify-center p-1.5 rounded-md text-green-600 hover:text-green-700 hover:bg-green-50/80 bg-transparent border-0 cursor-pointer transition-colors';
          return (
            <>
              <button
                type='button'
                className={viewBtn}
                aria-label='View'
                onClick={() => navigate(`/organizations/${org.apiId || org.id}`)}
              >
                <svg
                  className='shrink-0 w-4 h-4'
                  width='16'
                  height='16'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden={true}
                >
                  <path
                    d='M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
              <button
                type='button'
                className={editBtn}
                aria-label='Edit'
                onClick={() => handleEditClickCallback(org)}
              >
                <svg
                  className='shrink-0 w-4 h-4'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'></path>
                  <path d='M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z'></path>
                </svg>
              </button>
              {org.status === 'Active' && (
                <button
                  type='button'
                  className={deactivateBtn}
                  aria-label='Deactivate'
                  onClick={() => handleDeactivateOrganization(org)}
                >
                  <svg
                    className='shrink-0 w-4 h-4'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <circle cx='12' cy='12' r='10'></circle>
                    <line x1='4.93' y1='4.93' x2='19.07' y2='19.07'></line>
                  </svg>
                </button>
              )}
              {org.status === 'Inactive' && (
                <button
                  type='button'
                  className={activateBtn}
                  aria-label='Activate'
                  onClick={() => handleActivateOrganization(org)}
                >
                  <svg
                    className='shrink-0 w-4 h-4'
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <circle cx='12' cy='12' r='10'></circle>
                    <polyline points='9 12 11 14 15 10'></polyline>
                  </svg>
                </button>
              )}
            </>
          );
        },
      },
    ],
    [navigate, handleEditClickCallback, handleDeactivateOrganization, handleActivateOrganization]
  );

  const globalFilterFn: FilterFn<IOrganization> = (row, _columnId, filterValue) => {
    const org = row.original;
    const search = String(filterValue ?? '')
      .trim()
      .toLowerCase();
    if (search === '') {
      return true;
    }
    return (
      org.organizationName.toLowerCase().includes(search) ||
      org.companyName.toLowerCase().includes(search) ||
      org.email.toLowerCase().includes(search)
    );
  };

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
      <div className='flex items-center justify-between mb-6'>
        <nav className='flex items-center gap-2 text-sm'>
          <Link
            to={ROUTES.DASHBOARD}
            className='flex items-center text-theme-500 hover:opacity-80 transition-opacity [&_svg]:stroke-theme-500'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <path d='M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8'></path>
              <path d='M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'></path>
            </svg>
          </Link>
          <span className='text-gray-400 text-base'>›</span>
          <span className='text-gray-500 font-normal'>Organizations</span>
        </nav>
        <button
          type='button'
          className='bg-theme-500 text-white border-none px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer hover:bg-theme-600 transition-colors whitespace-nowrap'
          onClick={() => setIsModalOpen(true)}
        >
          <span>
            + <strong>Add Organization</strong>
          </span>
        </button>
      </div>

      <TanStackTable<IOrganization>
        data={organizations}
        columns={columns}
        loading={authLoading || isLoading}
        emptyMessage='No organizations found.'
        searchPlaceholder='Search organizations...'
        globalFilterFn={globalFilterFn}
        getRowId={(row) => String(row.apiId ?? row.id)}
      />

      <AddOrganizationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddOrganization}
        existingOrganizations={organizations}
      />

      <EditOrganizationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedOrganization(null);
        }}
        onUpdate={handleUpdateOrganization}
        organization={selectedOrganization}
      />

      <ConfirmModal
        isOpen={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={handleConfirmDeactivate}
        title='Deactivate Organization'
        message={`Are you sure you want to deactivate "${pendingConfirm?.org.organizationName || pendingConfirm?.org.companyName}"? The organization will be set to inactive status.`}
        confirmText='Deactivate'
        variant='danger'
      />

      <ResendActivationModal
        isOpen={pendingActivation !== null}
        onClose={() => setPendingActivation(null)}
        onSend={handleActivationSubmit}
        defaultEmail={pendingActivation?.email ?? ''}
        defaultAdminName={pendingActivation?.adminName || getAdminNameFromStorage()}
        isSending={isSendingActivation}
      />
    </div>
  );
};

export default OrganizationsPage;
